import { useState } from "react";
import { toast } from "sonner";
import { ShieldAlert } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";

export default function Copyright() {
  const [form, setForm] = useState({ reporter_name: "", reporter_email: "", work_identification: "", infringing_url: "", explanation: "", signature: "" });
  const [decl, setDecl] = useState(false);
  const [busy, setBusy] = useState(false);
  const [caseId, setCaseId] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!decl) { toast.error("You must confirm the legal declaration."); return; }
    setBusy(true);
    try { const { data } = await api.post("/copyright-report", { ...form, legal_declaration: true }); setCaseId(data.case_id); toast.success("Report received."); }
    catch (er) { toast.error(apiErr(er)); } finally { setBusy(false); }
  };

  if (caseId) return (
    <div className="max-w-lg mx-auto px-6 py-20 text-center">
      <ShieldAlert className="w-12 h-12 text-neon-pink mx-auto mb-4" />
      <h1 className="font-head text-2xl font-bold">Report received</h1>
      <p className="text-muted-foreground mt-2">Case ID: <span className="font-mono text-neon-cyan">{caseId}</span></p>
      <p className="text-sm text-muted-foreground mt-4">We will review your report following our copyright workflow (Reported → Under Review → Action Taken → Resolved).</p>
    </div>
  );

  return (
    <div className="max-w-xl mx-auto px-4 sm:px-6 py-14">
      <h1 className="font-head text-4xl font-black tracking-tighter mb-2">Report Copyright Issue</h1>
      <p className="text-muted-foreground mb-8">Use this form to report content that infringes your rights.</p>
      <form onSubmit={submit} className="glass rounded-3xl p-8 space-y-5">
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label className="text-xs">Your name</Label><Input required value={form.reporter_name} onChange={set("reporter_name")} className="mt-1.5 bg-input border-white/10" data-testid="cr-name" /></div>
          <div><Label className="text-xs">Your email</Label><Input type="email" required value={form.reporter_email} onChange={set("reporter_email")} className="mt-1.5 bg-input border-white/10" data-testid="cr-email" /></div>
        </div>
        <div><Label className="text-xs">Identify the work</Label><Input required value={form.work_identification} onChange={set("work_identification")} className="mt-1.5 bg-input border-white/10" placeholder="Title / description of your work" data-testid="cr-work" /></div>
        <div><Label className="text-xs">Infringing URL / content ID</Label><Input required value={form.infringing_url} onChange={set("infringing_url")} className="mt-1.5 bg-input border-white/10" data-testid="cr-url" /></div>
        <div><Label className="text-xs">Explanation</Label><Textarea required value={form.explanation} onChange={set("explanation")} className="mt-1.5 bg-input border-white/10 min-h-28" data-testid="cr-explain" /></div>
        <label className="flex items-start gap-3 text-sm cursor-pointer">
          <Checkbox checked={decl} onCheckedChange={setDecl} className="mt-0.5" data-testid="cr-decl" />
          <span className="text-muted-foreground">I declare, under penalty of perjury, that the information is accurate and I am authorized to act on behalf of the rights owner.</span>
        </label>
        <div><Label className="text-xs">Signature (full name)</Label><Input required value={form.signature} onChange={set("signature")} className="mt-1.5 bg-input border-white/10" data-testid="cr-sign" /></div>
        <Button type="submit" disabled={busy} className="w-full rounded-full bg-neon-pink hover:bg-neon-pink/90 font-bold py-6" data-testid="cr-submit">{busy ? "Submitting..." : "Submit report"}</Button>
      </form>
    </div>
  );
}
